import { useForm } from "@inertiajs/react";
import {
  Box,
  Container,
  Heading,
  Button,
  Text,
  Link as CLink,
  VStack,
  Input,
  HStack,
  Select,
  createListCollection,
  Field,
  Fieldset,
  Card,
  Stack,
} from "@chakra-ui/react";
import { Link } from "@inertiajs/react";
import { useAuth } from "@/hooks/useAuth";
import { prefectures, playerLevels, calculateAgeGroup } from "@/lib/utils";
import { useMemo } from "react";

interface Props {
  birth_year: number | null;
  birth_month: number | null;
  birth_day: number | null;
}

export default function PlayerEdit({ birth_year, birth_month, birth_day }: Props) {
  const player = useAuth().player!;

  const { data, setData, patch, processing, errors } = useForm({
    display_name: player.display_name,
    prefecture: player.prefecture,
    birth_year: birth_year ? birth_year.toString() : "",
    birth_month: birth_month ? birth_month.toString() : "",
    birth_day: birth_day ? birth_day.toString() : "",
    player_level: player.player_level,
  });

  const ageGroup = useMemo(() => {
    return calculateAgeGroup(
      parseInt(data.birth_year) || 0,
      parseInt(data.birth_month) || 0,
      parseInt(data.birth_day) || 0
    );
  }, [data.birth_year, data.birth_month, data.birth_day]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch("/my/settings/player");
  };

  const prefecturesList = createListCollection({
    items: Object.entries(prefectures).map(([key, value]) => ({ label: value, value: key })),
  });

  const playerLevelsList = createListCollection({
    items: Object.entries(playerLevels).map(([key, value]) => ({ label: value, value: key })),
  });

  // 年の選択肢（1900年から現在まで）
  const yearsList = createListCollection({
    items: Array.from({ length: 125 }, (_, i) => {
      const year = new Date().getFullYear() - i;
      return { label: year, value: year.toString() };
    }),
  });

  // 月の選択肢
  const monthsList = createListCollection({
    items: Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      return { label: month, value: month.toString() };
    }),
  });

  // 日の選択肢
  const daysList = createListCollection({
    items: Array.from({ length: 31 }, (_, i) => {
      const day = i + 1;
      return { label: day, value: day.toString() };
    }),
  });

  return (
    <Container maxW="2xl" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <Box>
          <CLink asChild fontSize="sm">
            <Link href="/my/settings">← 設定に戻る</Link>
          </CLink>
          <Heading size="2xl" mt={4}>
            プレイヤー情報
          </Heading>
          <Text color="gray.600" mt={2}>
            あなたのプロフィール情報を更新してください
          </Text>
        </Box>

        {/* Main Form Card */}
        <Card.Root bg="white" shadow="lg" borderRadius="xl">
          <Card.Body p={8}>
            <Box as="form" onSubmit={handleSubmit}>
              <Stack gap={6}>
                {/* Display Name */}
                <Field.Root invalid={!!errors.display_name}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    表示名
                  </Field.Label>
                  <Field.HelperText color="gray.500" fontSize="xs" mb={2}>
                    他のプレイヤーに表示される名前です
                  </Field.HelperText>
                  <Input
                    value={data.display_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData("display_name", e.target.value)}
                    placeholder="例: 宮城 リョータ"
                    autoComplete="username"
                    size="lg"
                    borderRadius="lg"
                    bg="gray.50"
                    _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                  />
                  <Field.ErrorText>{errors["display_name"]}</Field.ErrorText>
                </Field.Root>

                {/* Prefecture */}
                <Field.Root invalid={!!errors.prefecture}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    都道府県
                  </Field.Label>
                  <Select.Root
                    collection={prefecturesList}
                    value={[data.prefecture]}
                    onValueChange={value => setData("prefecture", value.value[0] || "")}
                    size="lg"
                  >
                    <Select.Control>
                      <Select.Trigger
                        borderRadius="lg"
                        bg="gray.50"
                        _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                      >
                        <Select.ValueText placeholder="都道府県を選択" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                        <Select.ClearTrigger />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content borderRadius="lg" shadow="xl">
                        {prefecturesList.items.map(prefecture => (
                          <Select.Item item={prefecture} key={prefecture.value}>
                            {prefecture.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.ErrorText>{errors["prefecture"]}</Field.ErrorText>
                </Field.Root>

                {/* Birthday */}
                <Fieldset.Root invalid={!!errors.birth_year || !!errors.birth_month || !!errors.birth_day}>
                  <Fieldset.Legend fontWeight="semibold" fontSize="sm">
                    生年月日
                  </Fieldset.Legend>
                  <Text color="gray.500" fontSize="xs">
                    年別カテゴリーを計算するために利用されます。他の人に生年月日は公開されません。
                  </Text>
                  <Fieldset.Content>
                    <HStack gap={2}>
                      {/* Year */}
                      <HStack gap={2}>
                        <Field.Root invalid={!!errors.birth_year}>
                          <Select.Root
                            collection={yearsList}
                            value={data.birth_year ? [data.birth_year] : []}
                            onValueChange={value => setData("birth_year", value.value[0] || "")}
                            size="md"
                            width={"5rem"}
                          >
                            <Select.Control>
                              <Select.Trigger
                                borderRadius="lg"
                                bg="gray.50"
                                _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                              >
                                <Select.ValueText placeholder="年" />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                              <Select.Content borderRadius="lg" shadow="xl" maxH="200px">
                                {yearsList.items.map(year => (
                                  <Select.Item key={year.value} item={year}>
                                    {year.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>
                        </Field.Root>
                        <Text fontSize="sm" color="gray.700">
                          年
                        </Text>
                      </HStack>

                      {/* Month */}
                      <HStack gap={2}>
                        <Field.Root invalid={!!errors.birth_month}>
                          <Select.Root
                            collection={monthsList}
                            value={data.birth_month ? [data.birth_month] : []}
                            onValueChange={value => setData("birth_month", value.value[0] || "")}
                            size="md"
                            width={"4rem"}
                          >
                            <Select.Control>
                              <Select.Trigger
                                borderRadius="lg"
                                bg="gray.50"
                                _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                              >
                                <Select.ValueText placeholder="月" />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                              <Select.Content borderRadius="lg" shadow="xl">
                                {monthsList.items.map(month => (
                                  <Select.Item key={month.value} item={month}>
                                    {month.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>
                        </Field.Root>
                        <Text fontSize="sm" color="gray.700">
                          月
                        </Text>
                      </HStack>

                      {/* Day */}
                      <HStack gap={2}>
                        <Field.Root invalid={!!errors.birth_day}>
                          <Select.Root
                            collection={daysList}
                            value={data.birth_day ? [data.birth_day] : []}
                            onValueChange={value => setData("birth_day", value.value[0] || "")}
                            size="md"
                            width={"4rem"}
                          >
                            <Select.Control>
                              <Select.Trigger
                                borderRadius="lg"
                                bg="gray.50"
                                _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                              >
                                <Select.ValueText placeholder="日" />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                              <Select.Content borderRadius="lg" shadow="xl">
                                {daysList.items.map(day => (
                                  <Select.Item key={day.value} item={day}>
                                    {day.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>
                        </Field.Root>
                        <Text fontSize="sm" color="gray.700">
                          日
                        </Text>
                      </HStack>
                    </HStack>
                  </Fieldset.Content>
                  <Fieldset.ErrorText>{errors.birth_day}</Fieldset.ErrorText>
                </Fieldset.Root>

                {/* Player Level */}
                <Field.Root invalid={!!errors.player_level}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    プレイヤーレベル
                  </Field.Label>
                  <Field.HelperText color="gray.500" fontSize="xs" mb={2}>
                    あなたの現在のスキルレベルを選択してください
                  </Field.HelperText>
                  <Select.Root
                    collection={playerLevelsList}
                    value={[data.player_level]}
                    onValueChange={value => setData("player_level", value.value[0] || "")}
                    size="lg"
                  >
                    <Select.Control>
                      <Select.Trigger
                        borderRadius="lg"
                        bg="gray.50"
                        _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                      >
                        <Select.ValueText placeholder="レベルを選択" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                        <Select.ClearTrigger />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content borderRadius="lg" shadow="xl">
                        {playerLevelsList.items.map(playerLevel => (
                          <Select.Item item={playerLevel} key={playerLevel.value}>
                            {playerLevel.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.ErrorText>{errors["player_level"]}</Field.ErrorText>
                </Field.Root>

                {/* Player Information Display */}
                <Box bg="gradient-to-r from-blue.50 to-purple.50" p={6} borderRadius="xl" border="1px solid" borderColor="blue.100">
                  <VStack gap={4} align="stretch">
                    {/* Age Group */}
                    <HStack justify="space-between" align="center">
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="medium">
                          年齢カテゴリー
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          生年月日から自動計算されます
                        </Text>
                      </Box>
                      {ageGroup && (
                        <Box bg="green.100" fontSize="md" px={3} py={1} borderRadius="full" fontWeight="medium">
                          {ageGroup}
                        </Box>
                      )}
                    </HStack>
                  </VStack>
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  colorPalette="blue"
                  size="xl"
                  borderRadius="xl"
                  loading={processing}
                  loadingText="更新中..."
                  _hover={{
                    transform: "translateY(-2px)",
                    shadow: "xl",
                  }}
                  transition="all 0.2s"
                  mt={4}
                >
                  プレイヤー情報を更新
                </Button>
              </Stack>
            </Box>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Container>
  );
}
